/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { toast } from "react-toastify";
import { initializeApp } from "firebase/app";
import {
  serverTimestamp,
  runTransaction,
  getFirestore,
  deleteField,
  collection,
  onSnapshot,
  arrayUnion,
  writeBatch,
  updateDoc,
  Timestamp,
  orderBy,
  getDocs,
  addDoc,
  setDoc,
  getDoc,
  query,
  doc,
  connectFirestoreEmulator,
  where,
} from "firebase/firestore";
import {
  signInWithPhoneNumber,
  signInWithCredential,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  RecaptchaVerifier,
  PhoneAuthProvider,
  signInWithPopup,
  getAuth,
  signOut,
  connectAuthEmulator,
} from "firebase/auth";
import { isMatchCompleted, isTeeTimeSubmitted } from "utils/match";
import { BallCountValue, BallCountType } from "enums/ballCountType";
import MatchPlayType from "enums/matchPlayType";
import { MatchType } from "enums/matchType";
import uuid from "react-uuid";

import {
  calculatePlaces,
  findUserIndexInTeeTimeUsers,
  findUserIndexInTeams,
  calculatePayouts,
  calculateMatchesForIndividuals,
  calculateMatchPlay,
  calculateMatchPlayForSingleTeeTime,
} from "../utils/teeTime";
import {
  calculateCourseHandicap,
  normalizePhoneNumber,
  calculateNetScore,
  calculateQuota,
  formatUserName,
} from "../utils";
import pinzApiService from "./pinzApiService";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_apiKey,
  authDomain: process.env.REACT_APP_authDomain,
  projectId: process.env.REACT_APP_projectId,
  storageBucket: process.env.REACT_APP_storageBucket,
  messagingSenderId: process.env.REACT_APP_messagingSenderId,
  appId: process.env.REACT_APP_appId,
  measurementId: process.env.REACT_APP_measurementId,
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const auth = getAuth();

if (process.env.NODE_ENV === "development") {
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectAuthEmulator(auth, "http://127.0.0.1:9099");
}

// connectFirestoreEmulator(db, "127.0.0.1", 8080);
// connectAuthEmulator(auth, "http://127.0.0.1:9099");

async function loginWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();

    const { user } = await signInWithPopup(auth, provider);

    return { uid: user.uid, displayName: user.displayName };
  } catch (error) {
    if (error.code !== "auth/cancelled-popup-request") {
      toast.error("Login with google error. Code: 4", {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }

    return null;
  }
}

async function loginAnonymously() {
  try {
    await signInAnonymously(auth);
  } catch (error) {
    toast.error("Login anonymously error. Code: 5", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
}

/**
 * Sends code to the specified phone number or throws an error
 * @param {*} phoneNumber in E.164 format
 * @returns firebase ConfirmationResult
 */
async function sendVerificationCode(phoneNumber) {
  if (!window.recaptchaVerifier) {
    window.recaptchaVerifier = new RecaptchaVerifier(
      "recaptcha-verifier-container",
      {
        size: "invisible",
        callback: (response) => {
          console.log(`RecaptchaVerifier: ${JSON.stringify(response)}`);
        },
        "expired-callback": () => {
          console.log("RecaptchaVerifier: expired-callback");
        },
      },
      auth,
    );
  }

  const { recaptchaVerifier } = window;
  return signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
}

async function upgradeAnonymousAcount(verificationId, confirmationCode) {
  const credential = PhoneAuthProvider.credential(
    verificationId,
    confirmationCode,
  );
  try {
    return await signInWithCredential(auth, credential);
  } catch (error) {
    // toast.error("Upgrade anonymous account error. Code: 6", {
    //   position: "bottom-center",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: true,
    //   draggable: true,
    //   progress: undefined,
    //   theme: "colored",
    // });
    throw new Error();
  }
}

async function logout() {
  try {
    await signOut(auth);
  } catch (error) {
    toast.error("Logout error. Code: 7", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
}

async function saveUserData2({ uid, patch }) {
  const userRef = doc(db, "users", uid);

  try {
    await runTransaction(db, async (transaction) => {
      const userSnapshot = await transaction.get(userRef);

      if (!userSnapshot.exists()) {
        transaction.set(userRef, patch);
      }

      const userData = userSnapshot.data();
      const updatedUserData = { ...userData, ...patch };

      transaction.update(userRef, updatedUserData);
    });

    return { ...patch, uid };
  } catch (error) {
    console.error("Error updating document: saveUserData2", error);
    return false;
  }
}

async function saveUserToDb({ uid, patch }) {
  const userRef = doc(db, "users", uid);

  // Удаляем undefined значения из patch
  const sanitizedPatch = Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(patch).filter(([_, value]) => value !== undefined),
  );

  try {
    const userSnapshot = await getDoc(userRef);

    if (!userSnapshot.exists()) {
      // Если пользователь не существует, создаём новый документ
      await setDoc(userRef, sanitizedPatch);
    } else {
      // Если пользователь существует, обновляем его данные
      await updateDoc(userRef, sanitizedPatch);
    }

    return { ...sanitizedPatch, uid };
  } catch (error) {
    console.error("Error saving user to DB:", error);
    return false;
  }
}

async function getUserDataOnce(userUid) {
  const docRef = doc(db, "users", userUid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      ...data,
      uid: docSnap.id,
      phone: normalizePhoneNumber(data.phone),
    };
  }
  return null;
}

async function updateUser({ uid, phoneNumber, ghinNumber, lastName }) {
  console.log("🔥 update user data", {
    uid,
    phoneNumber,
    ghinNumber,
    lastName,
  });
  if (uid && phoneNumber && !ghinNumber && !lastName) {
    console.log(1, uid, phoneNumber, !ghinNumber, !lastName);
    const updatedUserData = await pinzApiService.getUserDataByPhone({
      firebaseId: uid,
      phoneNumber,
    });
    console.log(2, { updatedUserData });
    if (updatedUserData) {
      const saveResult = await saveUserData2({
        uid,
        patch: {
          handicap: updatedUserData.handicapIndex ?? 0,
          phone: phoneNumber,
          name: formatUserName(
            updatedUserData.firstName,
            updatedUserData.lastName,
          ),
          ghinNumber: updatedUserData.ghinNumber,
          venmoAccount: updatedUserData.venmoAccount,
          gender: updatedUserData.gender || "M",
        },
      });
      console.log(3, saveResult);
      if (!saveResult) {
        console.error("Error saving user data");

        return false;
      }
      if (!updatedUserData) {
        await saveUserData2({
          uid,
          patch: {
            phone: phoneNumber,
            name: formatUserName(
              updatedUserData.firstName,
              updatedUserData.lastName,
            ),
          },
        });
        return {
          phone: phoneNumber,
          name: formatUserName(
            updatedUserData.firstName,
            updatedUserData.lastName,
          ),
        };
      }

      return {
        handicap: updatedUserData.handicapIndex ?? 0,
        phone: phoneNumber,
        name: formatUserName(
          updatedUserData.firstName,
          updatedUserData.lastName,
        ),
        ghinNumber: updatedUserData.ghinNumber,
        gender: updatedUserData.gender || "M",
      };
    }
  }

  if (uid && phoneNumber && ghinNumber && lastName) {
    console.log(11, "ghin upd");
    const res = await getUserDataOnce(uid);
    console.log(22, { res });
    const updateHandicapIndex = await pinzApiService.getHandicapIndex({
      firebaseId: uid,
      phoneNumber,
      lastName: lastName || res.name.split(" ").slice(-1).join(" "),
      ghinNumber: ghinNumber || res.ghinNumber,
    });

    console.log(33, { updateHandicapIndex });
    if (updateHandicapIndex) {
      const saveResult = await saveUserData2({
        uid,
        patch: {
          handicap: updateHandicapIndex.handicapIndex || 0,
          phone: phoneNumber,
          name: formatUserName(
            updateHandicapIndex.firstName,
            updateHandicapIndex.lastName,
          ),
          ghinNumber: updateHandicapIndex.ghinNumber,
          venmoAccount: updateHandicapIndex.venmoAccount ?? null,
          gender: updateHandicapIndex.gender || "M",
        },
      });
      console.log(44, { saveResult });
      // console.log("Document updated successfully");
      // if (!saveResult) {
      //   console.error("Error saving user data");
      //   return false;
      // }
      if (saveResult) {
        return {
          handicap: updateHandicapIndex.handicapIndex ?? 0,
          phone: phoneNumber,
          name: formatUserName(
            updateHandicapIndex.firstName,
            updateHandicapIndex.lastName,
          ),
          ghinNumber: updateHandicapIndex.ghinNumber,
        };
      }
      return false;
    }
    if (!updateHandicapIndex) {
      return false;
    }
  }
  return false;
}

async function updateUserByPhone({ uid, phoneNumber }) {
  try {
    const res = await getUserDataOnce(uid);
    console.log({ res });

    const updateHandicapIndex = await pinzApiService.getUserDataByPhone({
      firebaseId: uid,
      phoneNumber,
    });

    if (updateHandicapIndex) {
      saveUserData2({
        uid,
        patch: {
          handicap: updateHandicapIndex.handicapIndex ?? 0,
          phone: phoneNumber,
          name: formatUserName(
            updateHandicapIndex.firstName,
            updateHandicapIndex.lastName,
          ),
          ghinNumber: updateHandicapIndex.ghinNumber,
          gender: updateHandicapIndex.gender,
        },
      });
      console.log("Document updated successfully");
      return {
        handicap: updateHandicapIndex.handicapIndex ?? 0,
        phone: phoneNumber,
        name: formatUserName(
          updateHandicapIndex.firstName,
          updateHandicapIndex.lastName,
        ),
        ghinNumber: updateHandicapIndex.ghinNumber,
      };
    }

    return {
      ...res,
    };
  } catch (error) {
    return false;
  }
}

/**
 * Combining user data from the authorization provider and from the user collection by UID
 * Update GDCP index
 * @param {*} callback
 * @returns {*}
 */
function onUserStateChanged(callback) {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const getData = await getUserDataOnce(user.uid);

      console.log("🔥", { getData });

      const res = await updateUser({
        uid: user.uid,
        phoneNumber: user.phoneNumber,
        ghinNumber: getData?.ghinNumber ?? 0,
        lastName: getData?.name?.split(" ").slice(-1).join(" ") ?? null,
      });

      callback({
        ...res,
        ...getData,
        uid: user.uid,
        // displayName: user.displayName ?? "Anonymous",
        phone: user.phoneNumber,
      });
    } else {
      callback(null);
    }
  });
}

async function saveUserData(data, patch) {
  console.log("saveUserData", data.uid);
  try {
    await setDoc(doc(db, "users", data.uid), patch);
    return { ...data, ...patch };
  } catch (error) {
    toast.error("Save user data error. Code: 8", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
  return data;
}

async function authorizeUser(verificationId, confirmationCode) {
  const credential = PhoneAuthProvider.credential(
    verificationId,
    confirmationCode,
  );
  try {
    const { user } = await signInWithCredential(auth, credential);

    const saveResult = await updateUser({
      uid: user.uid,
      phoneNumber: user.phoneNumber,
      ghinNumber: null,
      lastName: null,
    });

    console.log({ saveResult, user });

    if (!saveResult) {
      console.error("Error saving user data inside auth");
    }

    if (saveResult) {
      return { ...user, ...saveResult };
    }

    console.log("saving user data inside auth", { saveResult });
    throw new Error("Error saving user data inside auth");
  } catch (error) {
    // toast.error("Upgrade anonymous account error. Code: 6", {
    //   position: "bottom-center",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: true,
    //   draggable: true,
    //   progress: undefined,
    //   theme: "colored",
    // });
    throw new Error();
  }
}

function getUserData(user, callback) {
  return onSnapshot(doc(db, "users", user.uid), (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      callback({
        ...data,
        uid: docSnap.id,
        phone: normalizePhoneNumber(data.phone),
      });
    } else {
      callback(null);
    }
  });
}

/**
 * @deprecated
 */
async function sendMessage(roomId, user, text) {
  try {
    await addDoc(collection(db, "chat-rooms", roomId, "messages"), {
      uid: user.uid,
      displayName: user.displayName,
      text: text.trim(),
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    toast.error("Send message error. Code: 9", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
}

/**
 * @deprecated
 */
function getMessages(roomId, callback) {
  return onSnapshot(
    query(
      collection(db, "chat-rooms", roomId, "messages"),
      orderBy("timestamp", "asc"),
    ),
    (querySnapshot) => {
      const messages = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      callback(messages);
    },
  );
}

// function getEvents(callback) {
//   return onSnapshot(
//     query(
//       collection(db, "events"),
//       where("customEvent", "==", true),
//       orderBy("timestamp", "desc"),
//     ),
//     (querySnapshot) => {
//       const events = querySnapshot.docs.map((docSnap) => ({
//         id: docSnap.id,
//         ...docSnap.data(),
//       }));

//       callback(events);
//     },
//   );
// }

function getEvents(callback) {
  return onSnapshot(
    query(
      collection(db, "events"),
      where("customEvent", "==", true),
      orderBy("timestamp", "desc"),
    ),
    (querySnapshot) => {
      const eventDataMap = new Map();

      // Создаем подписки на события
      const unsubscribers = querySnapshot.docs.map((docSnap) => {
        const eventData = { id: docSnap.id, ...docSnap.data(), teeTimes: [] };
        eventDataMap.set(docSnap.id, eventData);

        // Подписка на teeTimes внутри каждого event
        return onSnapshot(
          collection(docSnap.ref, "teeTimes"),
          (teeTimesSnapshot) => {
            const teeTimes = teeTimesSnapshot.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }));

            // Обновляем событие в map
            eventDataMap.set(docSnap.id, {
              ...eventDataMap.get(docSnap.id),
              teeTimes,
            });

            // Передаем обновленные данные
            callback(Array.from(eventDataMap.values()));
          },
        );
      });

      // Передаем изначальные данные (без teeTimes)
      callback(Array.from(eventDataMap.values()));

      // Функция отписки от всех подписок
      return () => unsubscribers.forEach((unsub) => unsub());
    },
  );
}

/**
 * @deprecated
 */
function getRoom(roomId, callback) {
  return onSnapshot(doc(db, "pinz-rooms", roomId), (docSnap) => {
    callback({
      id: docSnap.id,
      ...docSnap.data(),
    });
  });
}

function getEvent(eventId, callback) {
  return onSnapshot(doc(db, "events", eventId), (docSnap) => {
    callback({
      id: docSnap.id,
      ...docSnap.data(),
    });
  });
}

function getTeeTime(eventId, teeTimeId, callback) {
  return onSnapshot(
    doc(db, "events", eventId, "teeTimes", teeTimeId),
    (docSnap) => {
      callback({
        id: docSnap.id,
        ...docSnap.data(),
      });
    },
  );
}

function prepareTeeTimesQuery(eventId) {
  return query(
    collection(db, "events", eventId, "teeTimes"),
    orderBy("teeTime"),
  );
}

function getTeeTimes(eventId, callback) {
  const q = prepareTeeTimesQuery(eventId);
  return onSnapshot(q, (querySnapshot) => {
    const teeTimes = querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
    callback(teeTimes);
  });
}

async function getTeeTimesOnce(eventId) {
  try {
    const q = prepareTeeTimesQuery(eventId);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    toast.error("Get tee times once error. Code: 10", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
  return null;
}

function updateTeeTimePlayerHoles(
  player,
  holeIndex,
  holeHandicap,
  holePar,
  holeScore,
  holeId,
  maxScore,
) {
  const { courseHandicap, holes, matchHandicap } = player;
  const {
    score,
    handicap: teeHoleHandicap,
    holePar: teeHolePar,
    holeId: teeHoleId,
  } = holes.at(holeIndex);

  // set new holeScore if specified in function params
  // update dependent hole metrics if score already exists
  // use holePar as a default score value in other cases
  const newHoleScore = holeScore ?? score ?? teeHolePar ?? holePar;

  const netScore = calculateNetScore(
    matchHandicap ?? courseHandicap,
    teeHoleHandicap ?? holeHandicap,
    newHoleScore,
  );
  const grossToPar = newHoleScore - (teeHolePar ?? holePar);
  const netToPar = netScore - (teeHolePar ?? holePar);
  const maxGrossScore =
    maxScore && grossToPar > maxScore
      ? (teeHolePar ?? holePar) + maxScore
      : newHoleScore;

  holes[holeIndex] = {
    ...holes[holeIndex],
    holeId: teeHoleId ?? holeId,
    handicap: teeHoleHandicap ?? holeHandicap,
    score: newHoleScore,
    netScore,
    grossToPar,
    netToPar,
    maxGrossScore,
    maxNetScore:
      maxScore && netToPar > maxScore
        ? maxGrossScore + newHoleScore - netScore
        : netScore,
    quota: calculateQuota(grossToPar),
  };
  return {
    ...player,
    holes,
  };
}

async function postHoleValue(
  eventId,
  teeTimeId,
  userData,
  holeIndex,
  holeHandicap,
  holePar,
  holeScore,
  holeId,
  maxScore = 0,
) {
  try {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await getDoc(matchDocRef);
    const { matchType, stake, pressLimit, pressType } = matchDoc.data();
    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await getDoc(teeTimeDocRef);
    if (teeTimeDoc.exists()) {
      const teeTime = teeTimeDoc.data();
      let { users, teams } = teeTime;
      if (users) {
        const currentUserIndex = findUserIndexInTeeTimeUsers(userData, users);

        if (currentUserIndex > -1) {
          users[currentUserIndex] = updateTeeTimePlayerHoles(
            users.at(currentUserIndex),
            holeIndex,
            holeHandicap,
            holePar,
            holeScore,
            holeId,
            maxScore,
          );

          if (
            [MatchType.MatchPlayGross, MatchType.MatchPlayNet].includes(
              matchType,
            ) &&
            users.length === 2 &&
            users.every((u) => u.holes[holeIndex].score !== undefined)
          ) {
            if (!holeIndex && matchType === MatchType.MatchPlayNet) {
              users = users.map((player) => ({
                ...player,
                holes: player.holes.map((h) => ({
                  ...h,
                  ...(h.score !== undefined
                    ? {
                        netScore: calculateNetScore(
                          player.matchHandicap,
                          h.handicap,
                          h.score,
                        ),
                      }
                    : {}),
                })),
              }));
            }
            users = calculateMatchPlayForSingleTeeTime(
              users,
              matchType,
              stake,
              pressLimit,
              pressType,
            );
            users = calculatePayouts(users);
          }

          await updateDoc(teeTimeDocRef, { users });
        }
      }
      if (teams) {
        const { teamIndex, userIndex } = findUserIndexInTeams(userData, teams);

        if (teamIndex > -1) {
          teams[teamIndex].users[userIndex] = updateTeeTimePlayerHoles(
            teams[teamIndex].users.at(userIndex),
            holeIndex,
            holeHandicap,
            holePar,
            holeScore,
            holeId,
            maxScore,
          );

          if (
            [MatchType.MatchPlayGross, MatchType.MatchPlayNet].includes(
              matchType,
            ) &&
            teams.length === 2 &&
            teams.every(
              (t) =>
                t.users.length === t.teamSize &&
                t.users.every((u) => u.holes[holeIndex].score !== undefined),
            )
          ) {
            if (!holeIndex && matchType === MatchType.MatchPlayNet) {
              let allUsers = teams.reduce(
                (prev, curr) => [...prev, ...curr.users],
                [],
              );
              allUsers = allUsers.map((player) => ({
                ...player,
                holes: player.holes.map((h) => ({
                  ...h,
                  ...(h.score !== undefined
                    ? {
                        netScore: calculateNetScore(
                          player.matchHandicap,
                          h.handicap,
                          h.score,
                        ),
                      }
                    : {}),
                })),
              }));
              teams = teams.map((t) => ({
                ...t,
                users: allUsers.filter((u) =>
                  t.users.map((x) => x.phone).includes(u.phone),
                ),
              }));
            }
            const scoreType =
              matchType === MatchType.MatchPlayGross ? "score" : "netScore";
            teams = teams.map((t) => {
              const holes = [];
              for (let i = 0; i < 18; i += 1) {
                const scores = t.users.reduce(
                  (prev, curr) => [
                    ...prev,
                    curr.holes[i]?.[scoreType] ?? undefined,
                  ],
                  [],
                );
                scores.sort((a, b) => a - b);
                if (scores[0] !== undefined) {
                  holes.push({ [scoreType]: scores[0] });
                }
              }
              return {
                ...t,
                holes,
              };
            });
            teams = calculateMatchPlayForSingleTeeTime(
              teams,
              matchType,
              stake * 2,
              pressLimit,
              pressType,
            );
            let allUsers = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const team of teams) {
              const userNet = team.net / team.teamSize;
              allUsers.push(
                ...team.users.map((u) => ({
                  ...u,
                  net: userNet,
                  bets: [
                    {
                      net: userNet,
                      stake,
                    },
                  ],
                })),
              );
            }
            allUsers = calculatePayouts(allUsers);
            teams = teams.map((t) => ({
              ...t,
              users: allUsers.filter((u) =>
                t.users.map((x) => x.phone).includes(u.phone),
              ),
            }));
          }

          await updateDoc(teeTimeDocRef, { teams });
        }
      }
    } else {
      console.error("No such room document!");
    }
  } catch (error) {
    console.log({ error });
    toast.error("Post hole value error. Code: 11", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
}

async function postHoleDefaultValues(
  eventId,
  teeTimeId,
  userData,
  holeIndex,
  holePar,
  holeHandicap,
  holeId,
  maxScore = 0,
) {
  try {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await getDoc(matchDocRef);
    const { matchType, stake, pressLimit, pressType } = matchDoc.data();
    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await getDoc(teeTimeDocRef);
    if (teeTimeDoc.exists()) {
      const teeTime = teeTimeDoc.data();
      const { users, teams } = teeTime;
      if (users) {
        const currentUserIndex = findUserIndexInTeeTimeUsers(userData, users);

        if (currentUserIndex > -1) {
          let updatedUsers = users.map((player) =>
            updateTeeTimePlayerHoles(
              player,
              holeIndex,
              holeHandicap,
              holePar,
              undefined,
              holeId,
              maxScore,
            ),
          );

          if (
            [MatchType.MatchPlayGross, MatchType.MatchPlayNet].includes(
              matchType,
            ) &&
            updatedUsers.length === 2 &&
            updatedUsers.every((u) => u.holes[holeIndex].score !== undefined)
          ) {
            if (!holeIndex && matchType === MatchType.MatchPlayNet) {
              updatedUsers = updatedUsers.map((player) => ({
                ...player,
                holes: player.holes.map((h) => ({
                  ...h,
                  ...(h.score !== undefined
                    ? {
                        netScore: calculateNetScore(
                          player.matchHandicap,
                          h.handicap,
                          h.score,
                        ),
                      }
                    : {}),
                })),
              }));
            }
            updatedUsers = calculateMatchPlayForSingleTeeTime(
              updatedUsers,
              matchType,
              stake,
              pressLimit,
              pressType,
            );
            updatedUsers = calculatePayouts(updatedUsers);
          }

          await updateDoc(teeTimeDocRef, {
            users: updatedUsers,
          });
        }
      }

      if (teams) {
        const { teamIndex } = findUserIndexInTeams(userData, teams);

        if (teamIndex > -1) {
          let updatedTeams = teams.map((team) => ({
            ...team,
            users: team.users.map((player) =>
              updateTeeTimePlayerHoles(
                player,
                holeIndex,
                holeHandicap,
                holePar,
                undefined,
                holeId,
                maxScore,
              ),
            ),
          }));

          if (
            [MatchType.MatchPlayGross, MatchType.MatchPlayNet].includes(
              matchType,
            ) &&
            updatedTeams.length === 2 &&
            updatedTeams.every(
              (t) =>
                t.users.length === t.teamSize &&
                t.users.every((u) => u.holes[holeIndex].score !== undefined),
            )
          ) {
            if (!holeIndex && matchType === MatchType.MatchPlayNet) {
              let allUsers = updatedTeams.reduce(
                (prev, curr) => [...prev, ...curr.users],
                [],
              );
              allUsers = allUsers.map((player) => ({
                ...player,
                holes: player.holes.map((h) => ({
                  ...h,
                  ...(h.score !== undefined
                    ? {
                        netScore: calculateNetScore(
                          player.matchHandicap,
                          h.handicap,
                          h.score,
                        ),
                      }
                    : {}),
                })),
              }));
              updatedTeams = updatedTeams.map((t) => ({
                ...t,
                users: allUsers.filter((u) =>
                  t.users.map((x) => x.phone).includes(u.phone),
                ),
              }));
            }
            const scoreType =
              matchType === MatchType.MatchPlayGross ? "score" : "netScore";
            updatedTeams = updatedTeams.map((t) => {
              const holes = [];
              for (let i = 0; i < 18; i += 1) {
                const scores = t.users.reduce(
                  (prev, curr) => [
                    ...prev,
                    curr.holes[i]?.[scoreType] ?? undefined,
                  ],
                  [],
                );
                scores.sort((a, b) => a - b);
                if (scores[0] !== undefined) {
                  holes.push({ [scoreType]: scores[0] });
                }
              }
              return {
                ...t,
                holes,
              };
            });
            updatedTeams = calculateMatchPlayForSingleTeeTime(
              updatedTeams,
              matchType,
              stake * 2,
              pressLimit,
              pressType,
            );
            let allUsers = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const team of updatedTeams) {
              const userNet = team.net / team.teamSize;
              allUsers.push(
                ...team.users.map((u) => ({
                  ...u,
                  net: userNet,
                  bets: [
                    {
                      net: userNet,
                      stake,
                    },
                  ],
                })),
              );
            }
            allUsers = calculatePayouts(allUsers);
            updatedTeams = updatedTeams.map((t) => ({
              ...t,
              users: allUsers.filter((u) =>
                t.users.map((x) => x.phone).includes(u.phone),
              ),
            }));
          }

          await updateDoc(teeTimeDocRef, { teams: updatedTeams });
        }
      }
    } else {
      console.error("No such room document!");
    }
  } catch (error) {
    toast.error("Post holes default value error. Code: 12", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
}

/**
 * Adds or updates user in tee time.
 * Fails:
 *  - if match or tee time do not exists
 *  - if user already plays in the match
 *  - if tee time is full and user not playing
 *  - if match is completed
 *  - if tee time is submitted
 *
 * @param {*} eventId event id
 * @param {*} teeTimeId tee time
 * @param {*} userData user date with phone, name and handicap
 * @returns the user added and tee time is full
 */
async function addUserInTeeTime(eventId, teeTimeId, userData) {
  await pinzApiService.createPinzUser({
    id: userData.uid,
    phone: userData.phone,
    displayName: userData.name,
    matchId: eventId,
    teeTimeId,
    teamId: null,
    venmoAccount: userData.venmoAccount,
    teeTimeTimestamp: new Date(),
  });
  return runTransaction(db, async (transaction) => {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await transaction.get(matchDocRef);
    if (!matchDoc.exists()) {
      throw new Error("Match does not exist!");
    }

    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);
    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exist!");
    }

    const { players, teeTimesSubmitted, teeTimesValid, fiveSomes, matchType } =
      matchDoc.data();
    const { users } = teeTimeDoc.data();
    const index = findUserIndexInTeeTimeUsers(userData, users);

    if (index === -1 && players?.includes(userData.phone)) {
      throw new Error("User is playing in another tee time!");
    }

    if (index === -1 && users?.length === (fiveSomes ? 5 : 4)) {
      throw new Error("Tee time is full!");
    }

    if (isMatchCompleted({ teeTimesSubmitted, teeTimesValid })) {
      throw new Error("Cannot join. Match is completed");
    }

    if (isTeeTimeSubmitted({ teeTimesSubmitted }, teeTimeId)) {
      throw new Error("Cannot join. Tee time is submitted.");
    }

    const { handicap } = userData;
    const { slope, rating, par, holes } = matchDoc.data();
    const courseHandicap = calculateCourseHandicap(
      handicap,
      slope,
      rating,
      par,
    );
    const quota = 36 - courseHandicap;
    const userPatch = {
      uid: userData.uid,
      phone: userData.phone,
      displayName: userData.name,
      courseHandicap,
      quota,
      timestamp: new Date(),
      handicapIndex: handicap,
    };

    if (index > -1) {
      users[index] = { ...users[index], ...userPatch };
    } else {
      users.push({
        ...userPatch,
        holes: holes.map(({ par: holePar }) => ({ holePar })),
        isHomeCourse: true,
      });
    }

    transaction.update(teeTimeDocRef, { users });
    transaction.update(matchDocRef, {
      players: arrayUnion(userData.phone),
      teeTimesValid: arrayUnion(teeTimeId),
    });

    if (matchType === MatchType.MatchPlayNet) {
      let teeTimes = [...new Set([...(teeTimesValid ?? []), teeTimeId])];

      teeTimes = await Promise.all(
        teeTimes.map(async (id) => {
          if (id !== teeTimeId) {
            const tRef = doc(db, "events", eventId, "teeTimes", id);
            const tDoc = await transaction.get(tRef);
            if (!tDoc.exists()) {
              throw new Error("Tee time does not exist!");
            }
            return { id, users: tDoc.data().users };
          }
          return { id: teeTimeId, users };
        }),
      );

      let allUsers = teeTimes.reduce(
        (prev, curr) => [...prev, ...curr.users],
        [],
      );
      const minCourseHandicap = Math.min(
        ...allUsers.map((u) => u.courseHandicap),
      );
      allUsers = allUsers.map((player) => ({
        ...player,
        matchHandicap: player.courseHandicap - minCourseHandicap,
      }));

      teeTimes = teeTimes.map((t) => ({
        ...t,
        users: allUsers.filter((u) =>
          t.users.map((x) => x.phone).includes(u.phone),
        ),
      }));

      teeTimes.forEach((t) => {
        const ref = doc(db, "events", eventId, "teeTimes", t.id);
        transaction.update(ref, { users: t.users });
      });
    }

    return {
      isAdded: index === -1,
      isFull: users?.length === (fiveSomes ? 5 : 4),
      alreadyPlay: index > -1,
    };
  });
}

function isFullTeam(teamIndex, team, fiveSomes, usersLength) {
  if (teamIndex === -1 && team.users.length >= team.teamSize) {
    if (
      !fiveSomes ||
      (team.teamSize === 4 && team.users.length === 5) ||
      (team.teamSize === 2 &&
        (team.users.length === 3 ||
          (team.users.length === 2 && usersLength === 5)))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Adds or updates user in team.
 * Fails:
 *  - if match or tee time or team do not exists
 *  - if user already plays in the match
 *  - if team is full and user not playing
 *  - if match is completed
 *  - if tee time is submitted
 *
 * @param {*} eventId event id
 * @param {*} teeTimeId tee time id
 * @param {*} teamId team id
 * @param {*} userData user date with phone, name and handicap
 * @returns the user added and tee time is full
 */
async function addUserInTeam(eventId, teeTimeId, teamId, userData) {
  await pinzApiService.createPinzUser({
    id: userData.uid,
    phone: userData.phone,
    displayName: userData.name,
    matchId: eventId,
    teeTimeId,
    teamId,
    venmoAccount: userData.venmoAccount,
    teeTimeTimestamp: new Date(),
  });

  return runTransaction(db, async (transaction) => {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await transaction.get(matchDocRef);
    if (!matchDoc.exists()) {
      throw new Error("Match does not exists!");
    }

    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);
    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exists!");
    }

    const { players, teeTimesSubmitted, teeTimesValid, fiveSomes, matchType } =
      matchDoc.data();
    const { teams } = teeTimeDoc.data();
    let usersLength = teams.reduce((prev, curr) => prev + curr.users.length, 0);
    const team = teams.find((t) => t.teamId === teamId);
    if (!team) {
      throw new Error("Team does not exists!");
    }
    const { teamIndex, userIndex } = findUserIndexInTeams(userData, teams);
    console.log("findUserIndexInTeams", { teamIndex, userIndex });

    if (teamIndex === -1 && players?.includes(userData.phone)) {
      throw new Error("User is playing in another team!");
    }

    if (isFullTeam(teamIndex, team, fiveSomes, usersLength)) {
      throw new Error("Team is full!");
    }

    if (isMatchCompleted({ teeTimesSubmitted, teeTimesValid })) {
      throw new Error("Cannot join. Match is completed");
    }

    if (isTeeTimeSubmitted({ teeTimesSubmitted }, teeTimeId)) {
      throw new Error("Cannot join. Tee time is submitted.");
    }

    const { handicap } = userData;
    const { slope, rating, par, holes } = matchDoc.data();

    const courseHandicap = calculateCourseHandicap(
      handicap,
      slope,
      rating,
      par,
    );

    const quota = 36 - courseHandicap;
    const userPatch = {
      uid: userData.uid,
      phone: userData.phone,
      displayName: userData.name,
      courseHandicap,
      quota,
      timestamp: new Date(),
      handicapIndex: handicap,
    };

    if (teamIndex > -1 && userIndex > -1) {
      teams[teamIndex].users[userIndex] = {
        ...teams[teamIndex].users[userIndex],
        ...userPatch,
      };
    } else {
      team.users.push({
        ...userPatch,
        holes: holes?.map(({ par: holePar }) => ({ holePar })),
        isHomeCourse: true,
      });
    }

    transaction.update(teeTimeDocRef, { teams });
    transaction.update(matchDocRef, { players: arrayUnion(userData.phone) });
    if (team.users.length) {
      transaction.update(matchDocRef, {
        teeTimesValid: arrayUnion(teeTimeId),
      });
    }

    usersLength += 1;

    if (matchType === MatchType.MatchPlayNet) {
      let teeTimes = [...new Set([...(teeTimesValid ?? []), teeTimeId])];

      teeTimes = await Promise.all(
        teeTimes.map(async (id) => {
          if (id !== teeTimeId) {
            const tRef = doc(db, "events", eventId, "teeTimes", id);
            const tDoc = await transaction.get(tRef);
            if (!tDoc.exists()) {
              throw new Error("Tee time does not exist!");
            }
            return { id, teams: tDoc.data().teams };
          }
          return { id: teeTimeId, teams };
        }),
      );
      let allTeams = teeTimes.reduce(
        (prev, curr) => [...prev, ...curr.teams],
        [],
      );
      let allUsers = allTeams.reduce(
        (prev, curr) => [...prev, ...curr.users],
        [],
      );
      const minCourseHandicap = Math.min(
        ...allUsers.map((u) => u.courseHandicap),
      );
      allUsers = allUsers.map((player) => ({
        ...player,
        matchHandicap: player.courseHandicap - minCourseHandicap,
      }));

      allTeams = allTeams.map((t) => ({
        ...t,
        users: allUsers.filter((u) =>
          t.users.map((x) => x.phone).includes(u.phone),
        ),
      }));

      teeTimes = teeTimes.map((t) => ({
        ...t,
        teams: allTeams.filter((at) =>
          t.teams.map((x) => x.teamId).includes(at.teamId),
        ),
      }));

      teeTimes.forEach((t) => {
        const ref = doc(db, "events", eventId, "teeTimes", t.id);
        transaction.update(ref, { teams: t.teams });
      });
    }

    return {
      isAdded: teamIndex === -1,
      isFull: isFullTeam(teamIndex, team, fiveSomes, usersLength),
      alreadyPlay: teamIndex > -1,
    };
  });
}

/**
 * Calculates tee time results for individuals
 * Fails:
 *  - if match or tee time do not exists
 *  - if tee time is not complete
 *  - if tee time is submitted
 *  - if match is completed
 *
 * @param {*} eventId match id
 * @param {*} teeTimeId tee time id
 * @returns isMatchCompleted true if after submitting this tee time
 *          all tee times becomes submitted
 */
async function calculateMatchTeeTimeForIndividuals(eventId, teeTimeId) {
  return runTransaction(db, async (transaction) => {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await transaction.get(matchDocRef);
    if (!matchDoc.exists()) {
      throw new Error("Match does not exist!");
    }

    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);
    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exist!");
    }

    const {
      teeTimesSubmitted,
      teeTimesValid,
      matchType,
      stake,
      carryOver,
      scoreToWin,
      wagerType,
      bets,
      firstPlace,
      secondPlace,
      pressLimit,
      pressType,
    } = matchDoc.data();
    const { users } = teeTimeDoc.data();

    if (isMatchCompleted({ teeTimesSubmitted, teeTimesValid })) {
      throw new Error("Match is completed!");
    }

    if (isTeeTimeSubmitted({ teeTimesSubmitted, teeTimeId })) {
      throw new Error("Tee time is already submitted!");
    }

    if (matchType === undefined) {
      throw new Error("No match type found!");
    }

    users.forEach(({ courseHandicap, quota, displayName }) => {
      if (courseHandicap === undefined || quota === undefined) {
        throw new Error(`${displayName} does not have course handicap!`);
      }
    });

    const isMatchWillBeCompleted = isMatchCompleted({
      teeTimesSubmitted: [...(teeTimesSubmitted ?? []), teeTimeId],
      teeTimesValid,
    });

    let teeTimes = isMatchWillBeCompleted
      ? await Promise.all(
          teeTimesValid.map(async (id) => {
            if (id !== teeTimeId) {
              const tRef = doc(db, "events", eventId, "teeTimes", id);
              const tDoc = await transaction.get(tRef);
              if (!tDoc.exists()) {
                throw new Error("Tee time does not exist!");
              }
              return { id, users: tDoc.data().users };
            }
            return { id: teeTimeId, users };
          }),
        )
      : null;
    let allUsers = [];

    let skins = [];
    let totalStake = 0;
    let gameStake = 0;

    if (teeTimes) {
      allUsers = teeTimes.reduce((prev, curr) => [...prev, ...curr.users], []);

      ({ allUsers, skins, gameStake } = calculateMatchesForIndividuals(
        allUsers,
        matchType,
        stake,
        scoreToWin,
        carryOver,
        wagerType,
        firstPlace,
        secondPlace,
        pressLimit,
        pressType,
      ));

      totalStake += gameStake;

      if (bets.length) {
        bets.forEach((bet) => {
          const { matchType: betMatchType, stake: betStake } = bet;
          ({ allUsers, skins, gameStake } = calculateMatchesForIndividuals(
            allUsers,
            betMatchType,
            betStake,
            scoreToWin,
            carryOver,
            wagerType,
            firstPlace,
            secondPlace,
            pressLimit,
            pressType,
          ));
          totalStake += gameStake;
        });
      }

      allUsers = calculatePayouts(allUsers);

      teeTimes = teeTimes.map((t) => ({
        ...t,
        users: allUsers.filter((u) =>
          t.users.map((x) => x.phone).includes(u.phone),
        ),
      }));

      teeTimes.forEach((t) => {
        const ref = doc(db, "events", eventId, "teeTimes", t.id);
        transaction.update(ref, { users: t.users });
      });
    }

    transaction.update(matchDocRef, {
      teeTimesSubmitted: arrayUnion(teeTimeId),
      ...(teeTimes && skins.length ? { skins } : {}),
      ...(totalStake ? { totalStake } : {}),
    });

    return {
      players: allUsers.map(({ phone, place, win, net, holes, send }) => ({
        phone,
        place,
        win,
        net,
        holes,
        send,
      })),
      isMatchCompleted: isMatchWillBeCompleted,
    };
  });
}

/**
 * Calculates tee time results for teams
 * Fails:
 *  - if match or tee time do not exists
 *  - if tee time is not complete
 *  - if tee time is submitted
 *  - if match is completed
 *
 * @param {*} eventId match id
 * @param {*} teeTimeId tee time id
 * @returns isMatchCompleted true if after submitting this tee time
 *          all tee times becomes submitted
 */
async function calculateMatchTeeTimeForTeams(eventId, teeTimeId) {
  return runTransaction(db, async (transaction) => {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await transaction.get(matchDocRef);
    if (!matchDoc.exists()) {
      throw new Error("Match does not exist!");
    }

    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);
    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exist!");
    }

    const {
      ballCount,
      teeTimesSubmitted,
      teeTimesValid,
      matchType,
      stake,
      scoreToWin,
      carryOver,
      wagerType,
      bets,
      firstPlace,
      secondPlace,
      pressLimit,
      pressType,
      backsideDouble,
    } = matchDoc.data();
    const { teams } = teeTimeDoc.data();
    const users = teams.reduce((prev, curr) => [...prev, ...curr.users], []);

    if (isMatchCompleted({ teeTimesSubmitted, teeTimesValid })) {
      throw new Error("Match is completed!");
    }

    if (isTeeTimeSubmitted({ teeTimesSubmitted, teeTimeId })) {
      throw new Error("Tee time is already submitted!");
    }

    if (matchType === undefined) {
      throw new Error("No match type found!");
    }

    users.forEach(({ courseHandicap, quota, displayName }) => {
      if (courseHandicap === undefined || quota === undefined) {
        throw new Error(`${displayName} does not have course handicap!`);
      }
    });

    const isMatchWillBeCompleted = isMatchCompleted({
      teeTimesSubmitted: [...(teeTimesSubmitted ?? []), teeTimeId],
      teeTimesValid,
    });

    let teeTimes = isMatchWillBeCompleted
      ? await Promise.all(
          teeTimesValid.map(async (id) => {
            if (id !== teeTimeId) {
              const tRef = doc(db, "events", eventId, "teeTimes", id);
              const tDoc = await transaction.get(tRef);
              if (!tDoc.exists()) {
                throw new Error("Tee time does not exist!");
              }
              return { id, teams: tDoc.data().teams };
            }
            return { id: teeTimeId, teams };
          }),
        )
      : null;
    let allTeams = [];
    let allUsers = [];
    const singleUsers = [];
    const notPlayingTeams = [];
    let skins = [];
    let totalStake = 0;
    let gameStake = 0;
    if (teeTimes) {
      allTeams = teeTimes.reduce((prev, curr) => [...prev, ...curr.teams], []);
      const teamStake = stake * allTeams[0].teamSize;
      allUsers = allTeams.reduce((prev, curr) => [...prev, ...curr.users], []);
      const { teamSize } = allTeams[0];

      allTeams = teeTimes.reduce(
        (prev, curr) => [
          ...prev,
          ...curr.teams.filter((t) => {
            if (t.users.length && t.users.length >= t.teamSize) {
              return true;
            }
            singleUsers.push(...t.users);
            notPlayingTeams.push(t);
            return false;
          }),
        ],
        [],
      );

      if (allUsers.length < 2 * teamSize || allTeams.length < 2) {
        let newMatchType = matchType;
        if (matchType === MatchType.MatchPlayGross && allUsers.length !== 2) {
          // eslint-disable-next-line prefer-destructuring
          newMatchType = MatchType.StrokePlayGross;
        } else if (
          matchType === MatchType.MatchPlayNet &&
          allUsers.length !== 2
        ) {
          // eslint-disable-next-line prefer-destructuring
          newMatchType = MatchType.StrokePlayNet;
        }
        transaction.update(matchDocRef, {
          teamsPlay: false,
          matchType: newMatchType,
        });
        teeTimes.forEach((t) => {
          const ref = doc(db, "events", eventId, "teeTimes", t.id);
          const teeTimeUsers = t.teams.reduce(
            (prev, curr) => [...prev, ...curr.users],
            [],
          );
          transaction.update(ref, {
            users: teeTimeUsers.map((u) => ({
              ...u,
              net: 0,
              bets: [],
              win: 0,
            })),
            teams: deleteField(),
          });
        });
        return { convertToIndividuals: true };
      }
      allUsers = [];

      switch (matchType) {
        case MatchType.StrokePlayNet:
        case MatchType.StrokePlayGross: {
          const scoreType =
            matchType === MatchType.StrokePlayGross
              ? "maxGrossScore"
              : "maxNetScore";
          allTeams = allTeams.map((t) => {
            const holes = [];
            let outScore = 0;
            let inScore = 0;
            for (let i = 0; i < 18; i += 1) {
              const scores = t.users.reduce(
                (prev, curr) => [...prev, curr.holes[i][scoreType]],
                [],
              );
              scores.sort((a, b) => a - b);
              const bestBall = scores[0];
              const bestTwoBalls = scores[0] + scores[1];
              const allBalls = scores.reduce((prev, curr) => prev + curr, 0);
              const bestAndWorstBall = scores[0] + scores.at(-1);
              const hole = {
                allBalls,
                bestAndWorstBall,
                bestBall,
                bestTwoBalls,
              };
              holes.push(hole);
              if (i < 9) {
                outScore += hole[BallCountValue[ballCount]];
              } else {
                inScore += hole[BallCountValue[ballCount]];
              }
            }
            const total = outScore + inScore;
            return { ...t, holes, outScore, inScore, total };
          });
          break;
        }
        case MatchType.Quota:
          allTeams = allTeams.map((t) => {
            let total = 0;
            t.users.forEach((u) => {
              total += u.holes.reduce((acc, h) => acc + h.quota, 0) - u.quota;
            });
            return { ...t, total };
          });
          break;
        case MatchType.MatchPlayGross:
        case MatchType.MatchPlayNet: {
          if (matchType === MatchType.MatchPlayNet) {
            allUsers = allTeams.reduce(
              (prev, curr) => [...prev, ...curr.users],
              [],
            );
            allUsers = allUsers.map((player) => ({
              ...player,
              holes: player.holes.map((h) => ({
                ...h,
                ...(h.score !== undefined
                  ? {
                      netScore: calculateNetScore(
                        player.matchHandicap,
                        h.handicap,
                        h.score,
                      ),
                    }
                  : {}),
              })),
            }));
            allTeams = allTeams.map((t) => ({
              ...t,
              users: allUsers.filter((u) =>
                t.users.map((x) => x.phone).includes(u.phone),
              ),
            }));
            allUsers = [];
          }
          const scoreType =
            matchType === MatchType.MatchPlayGross ? "score" : "netScore";
          const ball2 = ballCount === BallCountType.BestTwoBalls;
          allTeams = allTeams.map((t) => {
            const holes = [];
            let outScore = 0;
            let inScore = 0;
            for (let i = 0; i < 18; i += 1) {
              const scores = t.users.reduce(
                (prev, curr) => [...prev, curr.holes[i][scoreType]],
                [],
              );
              scores.sort((a, b) => a - b);
              holes.push({
                [scoreType]: scores[0],
                ...(ball2 ? { [`${scoreType}2`]: scores[1] } : {}),
              });
              if (i < 9) {
                outScore += scores[0];
              } else {
                inScore += scores[0];
              }
            }
            const total = outScore + inScore;
            return {
              ...t,
              holes,
              outScore,
              inScore,
              total,
              matches: [],
              ...(ball2 ? { ball2: 0, matches2: [] } : {}),
              ways: 0,
              ball1: 0,
              net: 0,
            };
          });

          for (let i = 0; i < allTeams.length; i += 1) {
            for (let j = 0; j < allTeams.length; j += 1) {
              if (allTeams[i].teamId !== allTeams[j].teamId) {
                let match = {
                  teamId: allTeams[j].teamId,
                  name: allTeams[j].name,
                  holes: [],
                };
                let match2 = {
                  teamId: allTeams[j].teamId,
                  name: allTeams[j].name,
                  holes: [],
                };
                for (let k = 0; k < 18; k += 1) {
                  if (
                    allTeams[i].holes[k][scoreType] <
                    allTeams[j].holes[k][scoreType]
                  ) {
                    match.holes.push({ holeResult: "W" });
                  } else if (
                    allTeams[i].holes[k][scoreType] >
                    allTeams[j].holes[k][scoreType]
                  ) {
                    match.holes.push({ holeResult: "L" });
                  } else {
                    match.holes.push({ holeResult: "T" });
                  }
                  if (ball2) {
                    if (
                      allTeams[i].holes[k][`${scoreType}2`] <
                      allTeams[j].holes[k][`${scoreType}2`]
                    ) {
                      match2.holes.push({ holeResult: "W" });
                    } else if (
                      allTeams[i].holes[k][`${scoreType}2`] >
                      allTeams[j].holes[k][`${scoreType}2`]
                    ) {
                      match2.holes.push({ holeResult: "L" });
                    } else {
                      match2.holes.push({ holeResult: "T" });
                    }
                  }
                }
                let { result: back9 } = calculateMatchPlay(
                  match.holes,
                  MatchPlayType.Back9,
                  pressLimit,
                  pressType,
                );
                let { result: front9 } = calculateMatchPlay(
                  match.holes,
                  MatchPlayType.Front9,
                  pressLimit,
                  pressType,
                );
                let { result: big18 } = calculateMatchPlay(
                  match.holes,
                  MatchPlayType.Big18,
                  pressLimit,
                  pressType,
                );
                if (backsideDouble) {
                  back9.score *= 2;
                  big18.score *= 2;
                }
                match = { ...match, back9, front9, big18 };
                allTeams[i].matches.push(match);
                let ways = back9.score + front9.score + big18.score;
                let net = ways * teamStake;
                allTeams[i].ways += ways;
                allTeams[i].ball1 += ways;
                allTeams[i].net += net;
                if (ball2) {
                  ({ result: back9 } = calculateMatchPlay(
                    match2.holes,
                    MatchPlayType.Back9,
                    pressLimit,
                    pressType,
                  ));
                  ({ result: front9 } = calculateMatchPlay(
                    match2.holes,
                    MatchPlayType.Front9,
                    pressLimit,
                    pressType,
                  ));
                  ({ result: big18 } = calculateMatchPlay(
                    match2.holes,
                    MatchPlayType.Big18,
                    pressLimit,
                    pressType,
                  ));
                  if (backsideDouble) {
                    back9.score *= 2;
                    big18.score *= 2;
                  }
                  match2 = { ...match2, back9, front9, big18 };
                  allTeams[i].matches2.push(match2);
                  ways = back9.score + front9.score + big18.score;
                  net = ways * teamStake;
                  allTeams[i].ways += ways;
                  allTeams[i].ball2 += ways;
                  allTeams[i].net += net;
                }
              }
            }
          }
          break;
        }
        default:
      }

      if (
        [
          MatchType.StrokePlayGross,
          MatchType.StrokePlayNet,
          MatchType.Quota,
        ].includes(matchType)
      ) {
        allTeams = calculatePlaces(
          matchType,
          teamStake,
          allTeams,
          firstPlace,
          secondPlace,
        );
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const team of allTeams) {
        const userNet = team.net / team.users.length;
        const userWin = team.win / team.users.length;
        allUsers.push(
          ...team.users.map((u) => ({
            ...u,
            net: userNet,
            ...(Number.isNaN(userWin) ? {} : { win: userWin }),
            ...(team.place === undefined ? {} : { place: team.place }),
            bets: [
              {
                net: userNet,
                ...(Number.isNaN(userWin) ? {} : { win: userWin }),
                stake,
              },
            ],
          })),
        );
      }

      totalStake += teamStake * allTeams.length;

      if (bets.length) {
        bets.forEach((bet) => {
          const { matchType: betMatchType, stake: betStake } = bet;
          allUsers = [
            ...allUsers,
            ...singleUsers.map((u) => ({ ...u, individual: true })),
          ];
          ({ allUsers, skins, gameStake } = calculateMatchesForIndividuals(
            allUsers,
            betMatchType,
            betStake,
            scoreToWin,
            carryOver,
            wagerType,
            firstPlace,
            secondPlace,
            pressLimit,
            pressType,
          ));
          totalStake += gameStake;
        });
      }

      allUsers = calculatePayouts(allUsers);

      allTeams = [...allTeams, ...notPlayingTeams];

      if (!bets.length) {
        allUsers = [...allUsers, ...singleUsers];
      }

      allTeams = allTeams.map((t) => ({
        ...t,
        users: allUsers.filter((u) =>
          t.users.map((x) => x.phone).includes(u.phone),
        ),
      }));

      teeTimes = teeTimes.map((t) => ({
        ...t,
        teams: allTeams.filter((at) =>
          t.teams.map((x) => x.teamId).includes(at.teamId),
        ),
      }));

      teeTimes.forEach((t) => {
        const ref = doc(db, "events", eventId, "teeTimes", t.id);
        transaction.update(ref, { teams: t.teams });
      });
    }

    transaction.update(matchDocRef, {
      teeTimesSubmitted: arrayUnion(teeTimeId),
      ...(teeTimes && skins.length ? { skins } : {}),
      ...(totalStake ? { totalStake } : {}),
    });

    return {
      players: allUsers.map(
        ({ phone, place, win, net, holes, send, isHomeCourse }) => ({
          phone,
          place,
          win,
          net,
          holes,
          send,
          isHomeCourse,
        }),
      ),
      isMatchCompleted: isMatchWillBeCompleted,
    };
  });
}

/**
 * Update tee.
 * Fails:
 *  - if match or tee time do not exists
 *
 * @param {*} eventId event id
 * @param {*} teeTimeId tee time
 * @param {*} userData user data with phone, name and handicap
 * @param {*} tee tee data with slope, rating, par, teeId, teeName and holes
 */
async function updateTee(eventId, teeTimeId, userData, tee) {
  console.log({ eventId, teeTimeId, userData, tee });
  return runTransaction(db, async (transaction) => {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await transaction.get(matchDocRef);
    if (!matchDoc.exists()) {
      throw new Error("Match does not exist!");
    }

    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);
    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exist!");
    }

    const { maxScore, matchType, teeTimesValid } = matchDoc.data();
    const { users, teams } = teeTimeDoc.data();
    const { handicap } = userData;
    const { slope, rating, par, teeId, teeName } = tee;
    let { holes } = tee;
    holes.sort((a, b) => a.holeNumber - b.holeNumber);
    holes = holes.map((h) => {
      const { par: holePar } = h;
      // eslint-disable-next-line no-param-reassign
      delete h.par;
      return { ...h, holePar };
    });
    const courseHandicap = calculateCourseHandicap(
      handicap,
      slope,
      rating,
      par,
    );
    const quota = 36 - courseHandicap;
    const userPatch = {
      courseHandicap,
      holes,
      par,
      quota,
      rating,
      slope,
      teeId,
      teeName,
    };
    if (users) {
      const currentUserIndex = findUserIndexInTeeTimeUsers(userData, users);
      if (currentUserIndex > -1) {
        const userHoles = [...users[currentUserIndex].holes];
        users[currentUserIndex] = {
          ...users[currentUserIndex],
          ...userPatch,
        };
        userHoles.forEach((hole, index) => {
          if (hole.score !== undefined) {
            users[currentUserIndex] = updateTeeTimePlayerHoles(
              users[currentUserIndex],
              index,
              undefined,
              undefined,
              hole.score,
              undefined,
              maxScore,
            );
          }
        });
        transaction.update(teeTimeDocRef, { users });

        if (matchType === MatchType.MatchPlayNet) {
          let teeTimes = await Promise.all(
            teeTimesValid.map(async (id) => {
              if (id !== teeTimeId) {
                const tRef = doc(db, "events", eventId, "teeTimes", id);
                const tDoc = await transaction.get(tRef);
                if (!tDoc.exists()) {
                  throw new Error("Tee time does not exist!");
                }
                return { id, users: tDoc.data().users };
              }
              return { id: teeTimeId, users };
            }),
          );

          let allUsers = teeTimes.reduce(
            (prev, curr) => [...prev, ...curr.users],
            [],
          );
          const minCourseHandicap = Math.min(
            ...allUsers.map((u) => u.courseHandicap),
          );
          allUsers = allUsers.map((player) => ({
            ...player,
            matchHandicap: player.courseHandicap - minCourseHandicap,
          }));

          teeTimes = teeTimes.map((t) => ({
            ...t,
            users: allUsers.filter((u) =>
              t.users.map((x) => x.phone).includes(u.phone),
            ),
          }));

          teeTimes.forEach((t) => {
            const ref = doc(db, "events", eventId, "teeTimes", t.id);
            transaction.update(ref, { users: t.users });
          });
        }
      }
    }

    if (teams) {
      const { teamIndex, userIndex } = findUserIndexInTeams(userData, teams);
      if (userIndex > -1) {
        const userHoles = [...teams[teamIndex].users[userIndex].holes];
        teams[teamIndex].users[userIndex] = {
          ...teams[teamIndex].users[userIndex],
          ...userPatch,
        };
        userHoles.forEach((hole, index) => {
          if (hole.score !== undefined) {
            teams[teamIndex].users[userIndex] = updateTeeTimePlayerHoles(
              teams[teamIndex].users[userIndex],
              index,
              undefined,
              undefined,
              hole.score,
              undefined,
              maxScore,
            );
          }
        });
        transaction.update(teeTimeDocRef, { teams });
        if (matchType === MatchType.MatchPlayNet) {
          let teeTimes = await Promise.all(
            teeTimesValid.map(async (id) => {
              if (id !== teeTimeId) {
                const tRef = doc(db, "events", eventId, "teeTimes", id);
                const tDoc = await transaction.get(tRef);
                if (!tDoc.exists()) {
                  throw new Error("Tee time does not exist!");
                }
                return { id, teams: tDoc.data().teams };
              }
              return { id: teeTimeId, teams };
            }),
          );
          let allTeams = teeTimes.reduce(
            (prev, curr) => [...prev, ...curr.teams],
            [],
          );
          let allUsers = allTeams.reduce(
            (prev, curr) => [...prev, ...curr.users],
            [],
          );
          const minCourseHandicap = Math.min(
            ...allUsers.map((u) => u.courseHandicap),
          );
          allUsers = allUsers.map((player) => ({
            ...player,
            matchHandicap: player.courseHandicap - minCourseHandicap,
          }));

          allTeams = allTeams.map((t) => ({
            ...t,
            users: allUsers.filter((u) =>
              t.users.map((x) => x.phone).includes(u.phone),
            ),
          }));

          teeTimes = teeTimes.map((t) => ({
            ...t,
            teams: allTeams.filter((at) =>
              t.teams.map((x) => x.teamId).includes(at.teamId),
            ),
          }));

          teeTimes.forEach((t) => {
            const ref = doc(db, "events", eventId, "teeTimes", t.id);
            transaction.update(ref, { teams: t.teams });
          });
        }
      }
    }
  });
}

async function updateHomeCourse(eventId, teeTimeId, userData, isHomeCourse) {
  try {
    const roomDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const roomDocSnap = await getDoc(roomDocRef);
    if (roomDocSnap.exists()) {
      const room = roomDocSnap.data();
      const { users, teams } = room;
      if (users) {
        const currentUserIndex = findUserIndexInTeeTimeUsers(userData, users);

        if (currentUserIndex > -1) {
          users[currentUserIndex].isHomeCourse = isHomeCourse;
          await updateDoc(roomDocRef, { users });
          return true;
        }
      }
      if (teams) {
        const { teamIndex, userIndex } = findUserIndexInTeams(userData, teams);

        if (teamIndex > -1) {
          teams[teamIndex].users[userIndex].isHomeCourse = isHomeCourse;
          await updateDoc(roomDocRef, { teams });
          return true;
        }
      }
    } else {
      console.error("No such room document!");
    }
  } catch (error) {
    toast.error("Home course update error. Code: 14", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
  return false;
}

// only for testing, Anton is lazy to create new matches
async function fixMatch(eventId) {
  try {
    const batch = writeBatch(db);

    const matchDocRef = doc(db, "events", eventId);
    const matchDocSnap = await getDoc(matchDocRef);
    const { holes } = matchDocSnap.data();
    const teeTimesValid = [];
    const teeTimesSubmitted = [];

    const querySnapshot = await getDocs(
      collection(db, "events", eventId, "teeTimes"),
    );
    querySnapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .forEach(async (teeTime) => {
        const teeTimeDocRef = doc(
          db,
          "events",
          eventId,
          "teeTimes",
          teeTime.id,
        );
        const updatedUsers = teeTime.users.map((player) => {
          let fixedPlayer = player;
          // add holes array that should always exist
          if (!player.holes) {
            fixedPlayer = {
              ...player,
              holes: holes.map(({ hole: holePar }) => ({ holePar })),
            };
          }

          // add holePar if missing
          const fixedHoles = player.holes.map((playerHole, index) => {
            if (playerHole.holePar === undefined) {
              return { ...playerHole, holePar: holes.at(index).par };
            }
            return playerHole;
          });
          fixedPlayer = {
            ...player,
            holes: fixedHoles,
          };

          return fixedPlayer;
        });

        batch.update(teeTimeDocRef, {
          users: updatedUsers,
        });
        if (updatedUsers?.length > 1) {
          teeTimesValid.push(teeTime.id);
        }
      });

    batch.update(matchDocRef, {
      fixTimestamp: Timestamp.now(),
      teeTimesValid,
      teeTimesSubmitted,
    });

    await batch.commit();
    console.log("Match is fixed.");
  } catch (error) {
    toast.error("Fix match error. Code: 13", {
      position: "bottom-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  }
}

async function createEvent(eventId, userData, type, eventName, teeTimeId) {
  const { phone, name, uid } = userData;

  const typeFormat = {
    0: "The 2 Man",
    1: "The 3 Man",
  };

  // const pars = [4, 3, 4, 5, 4, 3, 4, 4, 4, 4, 4, 3, 4, 3, 4, 4, 5, 4];
  // const handicaps = [
  //   13, 17, 9, 7, 5, 15, 1, 11, 3, 14, 12, 16, 2, 18, 4, 10, 8, 6,
  // ];

  // const holes = Array.from({ length: 18 }, (_, index) => ({
  //   par: pars[index], // Устанавливаем значение пар для текущей лунки
  //   strokes: null,
  //   max: null,
  //   handicap: handicaps[index], // Устанавливаем значение хендикапа для текущей лунки
  //   pinzVerified: null,
  //   yardage: 372,
  //   frontStrokes: null,
  //   isUsga: null,
  //   isActive: false,
  //   backMax: null,
  //   lastModifiedUtc: Timestamp.now(), // Используем Timestamp.now()
  //   lastPullFromExternalUtc: Timestamp.now(), // Используем Timestamp.now()
  //   backStrokes: null,
  //   createdBy: null,
  //   frontMax: null,
  //   externalHoleId: index + 1,
  //   modifiedBy: null,
  //   holeId: uuid(),
  //   teeId: "de1b9fd5-628e-4dd5-946e-3f4bdcbe4d3c",
  //   holeNumber: index + 1,
  //   createdUtc: Timestamp.now(), // Используем Timestamp.now()
  // }));

  const holes = [
    { Hole: 1, Par: 4, Handicap: 13 },
    { Hole: 2, Par: 3, Handicap: 17 },
    { Hole: 3, Par: 4, Handicap: 9 },
    { Hole: 4, Par: 5, Handicap: 7 },
    { Hole: 5, Par: 4, Handicap: 5 },
    { Hole: 6, Par: 3, Handicap: 15 },
    { Hole: 7, Par: 4, Handicap: 1 },
    { Hole: 8, Par: 4, Handicap: 11 },
    { Hole: 9, Par: 4, Handicap: 3 },
    { Hole: 10, Par: 4, Handicap: 14 },
    { Hole: 11, Par: 4, Handicap: 12 },
    { Hole: 12, Par: 3, Handicap: 16 },
    { Hole: 13, Par: 4, Handicap: 2 },
    { Hole: 14, Par: 3, Handicap: 18 },
    { Hole: 15, Par: 4, Handicap: 4 },
    { Hole: 16, Par: 4, Handicap: 10 },
    { Hole: 17, Par: 5, Handicap: 8 },
    { Hole: 18, Par: 4, Handicap: 6 },
  ].map((hole) => ({
    par: hole.Par,
    strokes: null,
    max: null,
    handicap: hole.Handicap,
    pinzVerified: null,
    yardage: 372,
    frontStrokes: null,
    isUsga: null,
    isActive: false,
    backMax: null,
    lastModifiedUtc: Timestamp.now(), // Используем Timestamp.now()
    lastPullFromExternalUtc: Timestamp.now(), // Используем Timestamp.now()
    backStrokes: null,
    createdBy: null,
    frontMax: null,
    externalHoleId: hole.Hole,
    modifiedBy: null,
    holeId: uuid(),
    teeId: "de1b9fd5-628e-4dd5-946e-3f4bdcbe4d3c",
    holeNumber: hole.Hole,
    createdUtc: Timestamp.now(), // Используем Timestamp.now()
  }));

  // Данные для события
  const eventData = {
    holes,
    scoreToWin: 0,
    fiveSomes: false,
    teamsPlay: false,
    rating: 70.9,
    teeId: "de1b9fd5-628e-4dd5-946e-3f4bdcbe4d3c",
    pressLimit: true,
    secondPlace: 20,
    courseId: "cdf787e6-f63e-462d-971c-471c1b8569cb",
    teeName: "Gold",
    teeTimesSubmitted: [],
    pressType: 2,
    timestamp: serverTimestamp(),
    maxScore: 0,
    ballCount: 0,
    title: eventName,
    firstPlace: 80,
    par: 70,
    slope: 126,
    date: serverTimestamp(),
    carryOver: true,
    stake: 10,
    teeTimesValid: [],
    backsideDouble: false,
    createdBy: {
      pinzUserId: null,
      lastName: name,
      firstName: "",
      profileImageUrl: "",
    },
    spots: 2,
    numberOfTeeTimes: 1,
    courseName: "CC of Mobile",
    wagerType: 0,
    totalStake: 60,
    matchType: 3,
    players: [userData.phone],
    // players: [],
    bets: [],
    customEvent: true,
    customEventCreatorStatus: typeFormat[type],
  };

  // Создание документа события
  const eventRef = doc(db, "events", eventId);

  console.log({ eventData });

  await setDoc(eventRef, eventData);

  const teeTimeData1 = {
    teeTimeString: "5:50 PM",
    // teams: [
    //   {
    //     teamSize: 1,
    //     teamId: "b9a4c4ce-dc8c-4ee0-bb12-0c51349e64bb",
    //     name: "Two Man Team",
    //     users: [
    //       {
    //         displayName: name,
    //         phone,
    //         timestamp: Timestamp.now(),
    //         uid,
    //         holes: holes.map((hole) => ({ ...hole, holePar: hole.par })),
    //         handicapIndex: 0,
    //         courseHandicap: 0,
    //         quota: 0,
    //       },
    //     ],
    //   },
    //   {
    //     teamSize: 1,
    //     teamId: "b9a4c4ce-dc8c-4ee0-bb12-0c51349e64bs",
    //     name: "Three Man Team",
    //     users: [],
    //   },
    // ],
    users: [
      {
        displayName: typeFormat[type],
        phone,
        timestamp: Timestamp.now(),
        uid,
        holes: holes.map((hole) => ({ ...hole, holePar: hole.par })),
        handicapIndex: 0,
        courseHandicap: 0,
        quota: 0,
      },
    ],
    teeTime: Timestamp.now(), // Используем Timestamp
  };

  const teeTimeRef1 = doc(db, "events", eventId, "teeTimes", teeTimeId);

  await setDoc(teeTimeRef1, teeTimeData1);

  console.log(`Event ${eventId} and its tee time created successfully.`);
}

/**
 * Adds or updates user in tee time.
 * Fails:
 *  - if match or tee time do not exists
 *  - if user already plays in the match
 *  - if tee time is full and user not playing
 *  - if match is completed
 *  - if tee time is submitted
 *
 * @param {*} eventId event id
 * @param {*} teeTimeId tee time
 * @param {*} userData user date with phone, name and handicap
 * @returns the user added and tee time is full
 */
async function addUserInTeeTimeCustomEvent(
  eventId,
  teeTimeId,
  userData,
  teamCode,
) {
  return runTransaction(db, async (transaction) => {
    const matchDocRef = doc(db, "events", eventId);
    const matchDoc = await transaction.get(matchDocRef);
    if (!matchDoc.exists()) {
      throw new Error("Match does not exist!");
    }

    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);
    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exist!");
    }

    const { players, teeTimesSubmitted, teeTimesValid, fiveSomes, matchType } =
      matchDoc.data();
    const { users } = teeTimeDoc.data();

    console.log({ users });
    const index = findUserIndexInTeeTimeUsers(userData, users);

    if (index === -1 && players?.includes(userData.phone)) {
      throw new Error("User is playing in another tee time!");
    }

    if (index === -1 && users?.length === (fiveSomes ? 5 : 4)) {
      throw new Error("Tee time is full!");
    }

    if (isMatchCompleted({ teeTimesSubmitted, teeTimesValid })) {
      throw new Error("Cannot join. Match is completed");
    }

    if (isTeeTimeSubmitted({ teeTimesSubmitted }, teeTimeId)) {
      throw new Error("Cannot join. Tee time is submitted.");
    }

    const { handicap } = userData;
    const { slope, rating, par, holes } = matchDoc.data();
    const courseHandicap = calculateCourseHandicap(
      handicap,
      slope,
      rating,
      par,
    );
    const quota = 36 - courseHandicap;

    const userPatch = {
      uid: userData.uid,
      phone: userData.phone,
      displayName: teamCode === 0 ? "The 2 Man" : "The 3 Man",
      courseHandicap: 0,
      quota: 0,
      timestamp: new Date(),
      handicapIndex: 0,
    };

    if (index > -1) {
      users[index] = { ...users[index], ...userPatch };
    } else {
      users.push({
        ...userPatch,
        holes: holes.map(({ par: holePar }) => ({ holePar })),
        isHomeCourse: true,
      });
    }

    transaction.update(teeTimeDocRef, { users });
    transaction.update(matchDocRef, {
      players: arrayUnion(userData.phone),
      teeTimesValid: arrayUnion(teeTimeId),
    });

    if (matchType === MatchType.MatchPlayNet) {
      let teeTimes = [...new Set([...(teeTimesValid ?? []), teeTimeId])];

      teeTimes = await Promise.all(
        teeTimes.map(async (id) => {
          if (id !== teeTimeId) {
            const tRef = doc(db, "events", eventId, "teeTimes", id);
            const tDoc = await transaction.get(tRef);
            if (!tDoc.exists()) {
              throw new Error("Tee time does not exist!");
            }
            return { id, users: tDoc.data().users };
          }
          return { id: teeTimeId, users };
        }),
      );

      let allUsers = teeTimes.reduce(
        (prev, curr) => [...prev, ...curr.users],
        [],
      );
      const minCourseHandicap = Math.min(
        ...allUsers.map((u) => u.courseHandicap),
      );
      allUsers = allUsers.map((player) => ({
        ...player,
        matchHandicap: player.courseHandicap - minCourseHandicap,
      }));

      teeTimes = teeTimes.map((t) => ({
        ...t,
        users: allUsers.filter((u) =>
          t.users.map((x) => x.phone).includes(u.phone),
        ),
      }));

      teeTimes.forEach((t) => {
        const ref = doc(db, "events", eventId, "teeTimes", t.id);
        transaction.update(ref, { users: t.users });
      });
    }

    return {
      isAdded: index === -1,
      isFull: users?.length === (fiveSomes ? 5 : 4),
      alreadyPlay: index > -1,
    };
  });
}

async function submitIndividualAsBlock(eventId, teeTimeId, userPhone) {
  console.log(eventId, teeTimeId);

  return runTransaction(db, async (transaction) => {
    const teeTimeDocRef = doc(db, "events", eventId, "teeTimes", teeTimeId);
    const teeTimeDoc = await transaction.get(teeTimeDocRef);

    if (!teeTimeDoc.exists()) {
      throw new Error("Tee time does not exist!");
    }

    const teeTimeData = teeTimeDoc.data();
    const users = teeTimeData?.users;

    if (!users || !Array.isArray(users)) {
      throw new Error("No users found in this tee time!");
    }

    console.log("Users before update:", users);

    const updData = users.map((userData) =>
      userData.phone === userPhone
        ? { ...userData, blockHoles: true }
        : userData,
    );

    console.log("Updated users:", updData);

    transaction.update(teeTimeDocRef, { users: updData });
  });
}

export {
  loginWithGoogle,
  loginAnonymously,
  sendVerificationCode,
  upgradeAnonymousAcount,
  logout,
  onUserStateChanged,
  saveUserData,
  getUserData,
  getUserDataOnce,
  sendMessage,
  getMessages,
  getEvents,
  getRoom,
  getTeeTimes,
  getTeeTimesOnce,
  getTeeTime,
  getEvent,
  postHoleValue,
  postHoleDefaultValues,
  addUserInTeeTime,
  addUserInTeam,
  calculateMatchTeeTimeForIndividuals,
  calculateMatchTeeTimeForTeams,
  fixMatch,
  updateTee,
  saveUserData2,
  authorizeUser,
  updateUser,
  updateUserByPhone,
  updateHomeCourse,
  saveUserToDb,
  createEvent,
  addUserInTeeTimeCustomEvent,
  submitIndividualAsBlock,
};
